import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, QueryFn } from '@angular/fire/compat/firestore';
import { Observable, firstValueFrom, map } from 'rxjs';

import { plainToClass } from 'class-transformer';
import { BotModel } from 'src/app/models/bot';
import { ClientEnvironmentModel } from 'src/app/models/client-environment';
import { PermissionsService } from './permissions.service';
import { NodesService } from './nodes.service';
import { EnvironmentService } from './environments.service';
import { COLLECTION_NAMES } from './constants';

@Injectable({
  providedIn: 'root',
})
export class BotsService {
  private botsCollection: AngularFirestoreCollection<BotModel>;
  private botsCollectionQuery: (ref: QueryFn) => AngularFirestoreCollection<BotModel>;

  constructor(
    private afs: AngularFirestore,
    private permissionsService: PermissionsService,
    private nodesService: NodesService,
    private environmentService: EnvironmentService,
  ) {
    this.botsCollection = afs.collection<BotModel>(COLLECTION_NAMES.BOTS);
    this.botsCollectionQuery = (ref: QueryFn) => afs.collection<BotModel>(COLLECTION_NAMES.BOTS, ref);
  }

  addBot(bot: BotModel): Promise<void> {
    const botId = bot.id; // avoiding error prone scenarios
    return this.botsCollection
      .doc(botId)
      .ref.get()
      .then(result => {
        if (result.exists) {
          throw new Error(`Assistant "${botId}" already exists`);
        } else {
          return this.botsCollection.doc(botId).set(Object.assign({}, bot));
        }
      });
  }

  updateBot(bot: BotModel): Promise<void> {
    return this.botsCollection.doc(bot.id).update(Object.assign({}, bot));
  }

  getBotById(botId: string): Observable<BotModel | null> {
    return this.botsCollection
      .doc(botId)
      .valueChanges()
      .pipe(
        map(bot => {
          return plainToClass(BotModel, bot);
        }),
      );
  }

  async getBotBy(corpId: string, hierarchyElementSystemName: string, botCode: string, clientEnvironment: ClientEnvironmentModel): Promise<BotModel | null> {
    const botId = `${corpId}-${hierarchyElementSystemName}-${botCode}_${clientEnvironment.selectedStage.systemName}`;
    return firstValueFrom(this.getBotById(botId));
  }

  getBotsByHierarchyElement(hierarchyElementSystemName: string): Observable<BotModel[]> {
    return this.botsCollectionQuery(ref => ref.where('hierarchyElementSystemName', '==', hierarchyElementSystemName))
      .valueChanges()
      .pipe(
        map(bots => {
          return bots.map(bot => plainToClass(BotModel, bot));
        }),
      );
  }

  async removeBot(botId: string, corpId: string, hierarchySystemName: string) {
    const botIdArray = botId.split('-');
    const botCode = botIdArray.pop();

    const doc = await this.environmentService.listCorpEnvironments(corpId);
    // get all bot environments
    let environments: ClientEnvironmentModel | undefined;
    if (doc.exists) {
      environments = doc.data() as ClientEnvironmentModel;
    }

    let allBotIds: string[] = [];
    if (environments) {
      allBotIds = environments.stages.map(stage => {
        return `${corpId}-${hierarchySystemName}-${botCode}_${stage.systemName}`;
      });
    }

    // get all nodes associated with all of the bot's environments and delete delete them
    const nodesInBot = await this.nodesService.getNodesByBotIds(allBotIds);
    const deleteBotNodes = nodesInBot.docs.map(node => {
      return this.nodesService.deleteNodeById(node.id);
    });

    // delete from bots collection
    const deleteBots = allBotIds.map(bId => {
      return this.botsCollection.doc(bId).delete();
    });

    const permissionHierarchy = `${corpId}-${hierarchySystemName}`;
    // delete permissions
    const botPermissions = await firstValueFrom(this.permissionsService.getPermissionsBy(null, corpId, permissionHierarchy, botCode));

    const deletePermisions = botPermissions.map(perm => {
      return this.permissionsService.removePermission(perm.id);
    });

    await Promise.all([...deletePermisions, ...deleteBotNodes, ...deleteBots]);
  }
}
